import React, { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import { Button } from '../common';
import { Heading } from '../common/Typography';
import { Card } from '../common/Card';
import PlayerSearchInput from '../common/PlayerSearchInput';
import PaymentAliasHintsEditor from './PaymentAliasHintsEditor';
import {
  getPaymentLedger,
  getPaymentAllocations,
  undoPayment,
  getBatchSessionPlayerPaidStatus,
} from '../../services/paymentLedgerService';
import {
  fetchPaymentSearchAliasesByClub,
  mergeAliasesIntoPlayers,
} from '../../services/clubMemberPaymentAliasService';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatCurrency = (amount, code = 'GBP') => {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: code }).format(amount ?? 0);
  } catch {
    return `${code} ${(amount ?? 0).toFixed(2)}`;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

const paymentSourceLabel = (source) => {
  switch (source) {
    case 'manual_session_toggle': return 'Manual';
    case 'lump_payment':          return 'Recorded';
    case 'legacy_migration':      return 'Legacy';
    default:                       return source ?? '—';
  }
};

const paymentMethodLabel = (method) => {
  switch (method) {
    case 'cash':          return 'Cash';
    case 'bank_transfer': return 'Bank Transfer';
    case 'other':         return 'Other';
    default:              return method ?? '—';
  }
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, highlight }) => (
  <div className={`rounded-lg border p-4 ${highlight ? 'border-danger-dark/30 bg-danger-light dark:bg-danger/20' : 'border-border-medium bg-surface'}`}>
    <p className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1">{label}</p>
    <p className={`text-2xl font-bold ${highlight ? 'text-danger-dark' : 'text-text-primary'}`}>{value}</p>
    {sub && <p className="text-xs text-text-muted mt-1">{sub}</p>}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const PlayerPaymentDashboard = ({ clubId, currentUser, onRecordPayment }) => {
  const [clubPlayers, setClubPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // Dashboard data
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);
  const [balance, setBalance] = useState(null);
  const [unpaidSessions, setUnpaidSessions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [expandedPaymentId, setExpandedPaymentId] = useState(null);
  const [allocations, setAllocations] = useState({});
  const [loadingAllocations, setLoadingAllocations] = useState({});
  const [undoing, setUndoing] = useState(null);

  // ── Fetch all club players once on mount (merge host-only payment search aliases) ──

  useEffect(() => {
    const fetchClubPlayers = async () => {
      setLoadingPlayers(true);
      try {
        const { data, error } = await supabase
          .from('players_club')
          .select('players:player_id(id, first_name, last_name, email)')
          .eq('club_id', clubId);

        if (error) throw error;
        const players = (data || [])
          .map(r => r.players)
          .filter(Boolean)
          .sort((a, b) => a.first_name.localeCompare(b.first_name));

        const aliasRes = await fetchPaymentSearchAliasesByClub(clubId);
        const merged = mergeAliasesIntoPlayers(players, aliasRes.map || {});
        setClubPlayers(merged);
      } catch (err) {
        console.error('Error fetching club players:', err);
      } finally {
        setLoadingPlayers(false);
      }
    };

    if (clubId) fetchClubPlayers();
  }, [clubId]);

  // ── Load dashboard for selected player ──────────────────────────────────────

  const loadDashboard = useCallback(async (player) => {
    if (!player) return;
    setLoadingDashboard(true);
    setDashboardError(null);
    setBalance(null);
    setUnpaidSessions([]);
    setPayments([]);
    setExpandedPaymentId(null);
    setAllocations({});

    try {
      const [membershipRes, sessionPlayersRes, paymentsRes] = await Promise.all([
        supabase
          .from('players_club')
          .select('credit_balance')
          .eq('player_id', player.id)
          .eq('club_id', clubId)
          .single(),

        supabase
          .from('session_players')
          .select(`
            id,
            sessions!inner(
              id, name, session_date, price_per_player, currency_code, session_status
            )
          `)
          .eq('player_id', player.id)
          .eq('club_id', clubId)
          .not('sessions.price_per_player', 'is', null)
          .order('sessions(session_date)', { ascending: false }),

        getPaymentLedger(clubId, { playerId: player.id, includeDeleted: false, limit: 100 }),
      ]);

      const credit = membershipRes.data?.credit_balance ?? 0;
      const allSessionPlayers = sessionPlayersRes.data || [];

      const statusMap = allSessionPlayers.length > 0
        ? (await getBatchSessionPlayerPaidStatus(
            allSessionPlayers.map(sp => ({
              id: sp.id,
              sessionPrice: sp.sessions.price_per_player,
            }))
          )).statusMap
        : {};

      const unpaid = allSessionPlayers.filter(sp => !statusMap[sp.id]?.isPaid);
      const unpaidAmount = unpaid.reduce(
        (sum, sp) => sum + parseFloat(sp.sessions.price_per_player || 0), 0
      );

      setBalance({ credit, unpaidAmount, unpaidCount: unpaid.length });
      setUnpaidSessions(unpaid.map(sp => ({
        sessionPlayerId: sp.id,
        sessionId: sp.sessions.id,
        name: sp.sessions.name,
        date: sp.sessions.session_date,
        price: parseFloat(sp.sessions.price_per_player),
        currency: sp.sessions.currency_code,
        status: sp.sessions.session_status,
      })));

      setPayments(paymentsRes.success ? paymentsRes.payments : []);
    } catch (err) {
      console.error('Error loading player dashboard:', err);
      setDashboardError('Failed to load player payment data.');
    } finally {
      setLoadingDashboard(false);
    }
  }, [clubId]);

  const handleSelectPlayer = (player) => {
    setSelectedPlayer(player);
    loadDashboard(player);
  };

  const handleClearPlayer = () => {
    setSelectedPlayer(null);
    setBalance(null);
    setUnpaidSessions([]);
    setPayments([]);
    setDashboardError(null);
  };

  // ── Expand payment allocations ───────────────────────────────────────────────

  const toggleAllocations = async (paymentId) => {
    if (expandedPaymentId === paymentId) {
      setExpandedPaymentId(null);
      return;
    }
    setExpandedPaymentId(paymentId);
    if (allocations[paymentId]) return;

    setLoadingAllocations(prev => ({ ...prev, [paymentId]: true }));
    const res = await getPaymentAllocations(paymentId);
    if (res.success) {
      setAllocations(prev => ({ ...prev, [paymentId]: res.allocations }));
    }
    setLoadingAllocations(prev => ({ ...prev, [paymentId]: false }));
  };

  // ── Undo payment ────────────────────────────────────────────────────────────

  const handleUndo = async (paymentId) => {
    if (!window.confirm('Undo this payment? Sessions will be marked unpaid and credit recalculated.')) return;
    setUndoing(paymentId);
    const res = await undoPayment(paymentId, currentUser?.id);
    setUndoing(null);
    if (res.success) {
      loadDashboard(selectedPlayer);
    } else {
      alert(`Failed to undo: ${res.error}`);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  const netBalance = balance ? balance.credit - balance.unpaidAmount : null;
  const defaultCurrency = unpaidSessions[0]?.currency || payments[0]?.currency_code || 'GBP';

  return (
    <div className="space-y-6">

      {/* ── Player search card ── */}
      <Card>
        <div className="p-4 sm:p-6">
          <Heading level={3} className="mb-1">Player Payment Dashboard</Heading>
          <p className="text-sm text-text-secondary mb-4">
            Search by app name, email, or saved bank/transfer name hints (below).
          </p>
          <div className="w-full max-w-full sm:max-w-sm">
            <PlayerSearchInput
              placeholder={loadingPlayers ? 'Loading players…' : 'Search by name, email, or bank hint…'}
              availablePlayers={clubPlayers}
              selectedPlayer={selectedPlayer}
              onSelectPlayer={handleSelectPlayer}
              onRemovePlayer={handleClearPlayer}
              disabled={loadingPlayers}
              includePaymentAliases
            />
          </div>
        </div>
      </Card>

      {/* ── Loading ── */}
      {loadingDashboard && (
        <div className="text-center py-12 text-text-muted">Loading player data…</div>
      )}

      {/* ── Error ── */}
      {dashboardError && (
        <div className="rounded-lg bg-danger-light border border-danger-dark/30 px-4 py-3 text-sm text-danger-dark">
          {dashboardError}
        </div>
      )}

      {/* ── Dashboard content ── */}
      {selectedPlayer && balance && !loadingDashboard && (
        <>
          {/* Header */}
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="min-w-0">
              <Heading level={3} className="mb-0 break-words">
                {selectedPlayer.first_name} {selectedPlayer.last_name}
              </Heading>
              <p className="text-sm text-text-muted mt-0.5">Payment summary</p>
            </div>
            <Button
              variant="primary"
              size="small"
              className="w-full sm:w-auto justify-center shrink-0"
              onClick={() => onRecordPayment && onRecordPayment(selectedPlayer)}
            >
              + Record Payment
            </Button>
          </div>

          <Card variant="muted" size="small" className="border border-border-medium">
            <div className="p-4">
              <PaymentAliasHintsEditor
                key={selectedPlayer.id}
                clubId={clubId}
                playerId={selectedPlayer.id}
                initialAliases={selectedPlayer.payment_search_aliases}
                onSaved={(aliases) => {
                  const pid = selectedPlayer.id;
                  setClubPlayers((prev) =>
                    prev.map((p) =>
                      p.id === pid ? { ...p, payment_search_aliases: aliases } : p
                    )
                  );
                  setSelectedPlayer((prev) =>
                    prev?.id === pid ? { ...prev, payment_search_aliases: aliases } : prev
                  );
                }}
              />
            </div>
          </Card>

          {/* ── Stats ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard
              label="Amount Owed"
              value={formatCurrency(balance.unpaidAmount, defaultCurrency)}
              sub={`${balance.unpaidCount} unpaid session${balance.unpaidCount !== 1 ? 's' : ''}`}
              highlight={balance.unpaidAmount > 0}
            />
            <StatCard
              label="Credit Balance"
              value={formatCurrency(balance.credit, defaultCurrency)}
              sub="Available to apply"
            />
            <StatCard
              label="Net Balance"
              value={formatCurrency(netBalance, defaultCurrency)}
              sub={netBalance < 0 ? 'Outstanding' : netBalance > 0 ? 'In credit' : 'All settled'}
              highlight={netBalance < 0}
            />
            <StatCard
              label="Total Payments"
              value={payments.length}
              sub="Recorded payments"
            />
          </div>

          {/* ── Unpaid sessions ── */}
          <Card>
            <div className="p-4 sm:p-5 border-b border-border-medium flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <Heading level={4} className="mb-0">Outstanding Sessions</Heading>
              {balance.unpaidCount > 0 && (
                <span className="text-xs font-bold text-danger-dark bg-danger-light border border-danger-dark/30 rounded-full px-2 py-0.5 w-fit">
                  {balance.unpaidCount} unpaid
                </span>
              )}
            </div>
            {unpaidSessions.length === 0 ? (
              <div className="p-6 text-center text-sm text-text-muted">
                ✅ No outstanding sessions — all settled!
              </div>
            ) : (
              <>
                <div className="lg:hidden divide-y divide-border-medium">
                  {unpaidSessions.map((s) => (
                    <div key={s.sessionPlayerId} className="p-4 space-y-2">
                      <div className="font-medium text-text-primary break-words">{s.name || 'Unnamed Session'}</div>
                      <div className="text-sm text-text-secondary">{formatDate(s.date)}</div>
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        s.status === 'completed' ? 'bg-warning-light text-warning-dark' : 'bg-surface-subtle text-text-secondary'
                      }`}>
                        {s.status}
                      </span>
                      <div className="text-sm font-semibold text-text-primary">
                        {formatCurrency(s.price, s.currency)}
                      </div>
                    </div>
                  ))}
                  <div className="p-4 bg-surface-subtle border-t-2 border-border-medium flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-text-secondary">Total Outstanding</span>
                    <span className="font-bold text-danger-dark">{formatCurrency(balance.unpaidAmount, defaultCurrency)}</span>
                  </div>
                </div>
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border-medium text-left text-xs text-text-muted uppercase tracking-wide">
                        <th className="px-5 py-3">Session</th>
                        <th className="px-5 py-3">Date</th>
                        <th className="px-5 py-3">Status</th>
                        <th className="px-5 py-3 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-medium">
                      {unpaidSessions.map(s => (
                        <tr key={s.sessionPlayerId} className="hover:bg-surface-subtle">
                          <td className="px-5 py-3 font-medium text-text-primary">{s.name || 'Unnamed Session'}</td>
                          <td className="px-5 py-3 text-text-secondary whitespace-nowrap">{formatDate(s.date)}</td>
                          <td className="px-5 py-3">
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                              s.status === 'completed' ? 'bg-warning-light text-warning-dark' : 'bg-surface-subtle text-text-secondary'
                            }`}>
                              {s.status}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-text-primary">
                            {formatCurrency(s.price, s.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-border-medium bg-surface-subtle">
                        <td colSpan={3} className="px-5 py-3 text-sm font-semibold text-text-secondary">Total Outstanding</td>
                        <td className="px-5 py-3 text-right font-bold text-danger-dark">
                          {formatCurrency(balance.unpaidAmount, defaultCurrency)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            )}
          </Card>

          {/* ── Payment history ── */}
          <Card>
            <div className="p-4 sm:p-5 border-b border-border-medium">
              <Heading level={4} className="mb-0">Payment History</Heading>
            </div>
            {payments.length === 0 ? (
              <div className="p-6 text-center text-sm text-text-muted">
                No payments recorded yet.
              </div>
            ) : (
              <div className="divide-y divide-border-medium">
                {payments.map(payment => {
                  const isExpanded = expandedPaymentId === payment.id;
                  return (
                    <div key={payment.id}>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3 px-4 sm:px-5 py-4 hover:bg-surface-subtle">
                        <div className="flex gap-3 min-w-0 flex-1 items-start">
                          <button
                            type="button"
                            onClick={() => toggleAllocations(payment.id)}
                            className="shrink-0 text-text-muted hover:text-text-secondary mt-0.5"
                            title={isExpanded ? 'Collapse' : 'Show sessions covered'}
                          >
                            <svg
                              className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          <div className="min-w-0 flex-1 space-y-1">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div>
                                <p className="text-sm font-medium text-text-primary">{formatDate(payment.created_at)}</p>
                                <p className="text-xs text-text-muted">{paymentSourceLabel(payment.payment_source)}</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleUndo(payment.id)}
                                disabled={undoing === payment.id}
                                className="sm:hidden text-xs text-danger-dark hover:opacity-80 disabled:opacity-40"
                                title="Undo this payment"
                              >
                                {undoing === payment.id ? '…' : 'Undo'}
                              </button>
                            </div>
                            <p className="text-sm text-text-secondary">{paymentMethodLabel(payment.payment_method)}</p>
                            {payment.note && (
                              <p className="text-xs text-text-muted break-words" title={payment.note}>{payment.note}</p>
                            )}
                            {payment.created_by_player && (
                              <p className="text-xs text-text-muted sm:hidden">
                                by {payment.created_by_player.first_name} {payment.created_by_player.last_name}
                              </p>
                            )}
                            <p className="font-semibold text-success-dark sm:hidden">
                              {formatCurrency(payment.amount, payment.currency_code)}
                            </p>
                          </div>
                        </div>
                        <div className="hidden sm:flex sm:items-center sm:gap-3 sm:shrink-0 pl-8 sm:pl-0">
                          {payment.created_by_player && (
                            <p className="text-xs text-text-muted shrink-0 max-w-[7rem] truncate" title={`${payment.created_by_player.first_name} ${payment.created_by_player.last_name}`}>
                              by {payment.created_by_player.first_name} {payment.created_by_player.last_name}
                            </p>
                          )}
                          <p className="font-semibold text-success-dark shrink-0">
                            {formatCurrency(payment.amount, payment.currency_code)}
                          </p>
                          <button
                            type="button"
                            onClick={() => handleUndo(payment.id)}
                            disabled={undoing === payment.id}
                            className="shrink-0 text-xs text-danger-dark hover:opacity-80 disabled:opacity-40"
                            title="Undo this payment"
                          >
                            {undoing === payment.id ? '…' : 'Undo'}
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="bg-surface-subtle border-t border-border-medium px-4 sm:px-12 py-3">
                          {loadingAllocations[payment.id] ? (
                            <p className="text-xs text-text-muted">Loading sessions covered…</p>
                          ) : (allocations[payment.id] || []).length === 0 ? (
                            <p className="text-xs text-text-muted">No session allocations (credit payment).</p>
                          ) : (
                            <ul className="space-y-1">
                              {(allocations[payment.id] || []).map(a => (
                                <li key={a.id} className="flex items-center justify-between text-xs text-text-secondary">
                                  <span>
                                    {a.session_player?.sessions?.name || 'Session'} —{' '}
                                    {formatDate(a.session_player?.sessions?.session_date)}
                                  </span>
                                  <span className="font-medium">
                                    {formatCurrency(a.amount_applied, payment.currency_code)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default PlayerPaymentDashboard;
